// Spreadsheet parsing for the pdf-editor import flow.
//
// SheetJS reads XLSX / XLSM / XLSB / XLS / ODS / FODS / CSV / TSV /
// DIF / SLK / PRN / DBF from one byte stream — format detection is
// automatic. The library is ~700 KiB minified, so it loads lazily: the
// dynamic import below keeps it out of the editor bundle until the
// user actually picks a spreadsheet.
//
// Cells come back as DISPLAY text (number formats applied — dates,
// currencies, percentages look like they did in the source app) plus
// the source type, which sheet-layout.ts uses for auto-alignment.

import type { SheetCellData, SheetData } from "./sheet-layout.ts";

/** Import caps — beyond this the sheet is truncated, not rejected. */
export const SHEET_MAX_ROWS = 10_000;
export const SHEET_MAX_COLS = 100;

export interface ParsedWorkbook {
  sheets: SheetData[];
  /** Rows or columns were dropped to stay inside the caps. */
  truncated: boolean;
}

/** File extensions the picker accepts (SheetJS-readable formats). */
export const SHEET_ACCEPT = [
  ".csv", ".tsv", ".txt", ".xlsx", ".xlsm", ".xlsb", ".xls",
  ".ods", ".fods", ".dif", ".slk", ".sylk", ".prn", ".dbf",
  "text/csv",
  "text/tab-separated-values",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.spreadsheet",
].join(",");

const SHEET_EXT_RE = /\.(csv|tsv|txt|xlsx|xlsm|xlsb|xls|ods|fods|dif|slk|sylk|prn|dbf)$/i;

export function isSheetFile(f: File): boolean {
  return SHEET_EXT_RE.test(f.name);
}

/** Strip the spreadsheet extension for the imported tab's name. */
export function sheetBaseName(name: string): string {
  return name.replace(SHEET_EXT_RE, "");
}

function cellType(t: string | undefined): SheetCellData["t"] {
  if (t === "n" || t === "b" || t === "d" || t === "e") return t;
  return "s";
}

export async function parseWorkbook(bytes: ArrayBuffer): Promise<ParsedWorkbook> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(new Uint8Array(bytes), { type: "array", cellDates: false });
  if (wb.SheetNames.length === 0) throw new Error("the workbook contains no sheets");

  let truncated = false;
  const sheets: SheetData[] = [];
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    const ref = ws?.["!ref"];
    if (!ws || !ref) {
      sheets.push({ name, rows: [] });
      continue;
    }
    const range = XLSX.utils.decode_range(ref);
    const rowEnd = Math.min(range.e.r, range.s.r + SHEET_MAX_ROWS - 1);
    const colEnd = Math.min(range.e.c, range.s.c + SHEET_MAX_COLS - 1);
    if (rowEnd < range.e.r || colEnd < range.e.c) truncated = true;

    const rows: SheetCellData[][] = [];
    for (let r = range.s.r; r <= rowEnd; r += 1) {
      const row: SheetCellData[] = [];
      for (let c = range.s.c; c <= colEnd; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r, c })] as
          | { t?: string; v?: unknown }
          | undefined;
        if (!cell || cell.v === undefined || cell.v === null) {
          row.push({ v: "", t: "s" });
          continue;
        }
        let text: string;
        try {
          text = XLSX.utils.format_cell(cell as never) ?? "";
        } catch {
          text = String(cell.v);
        }
        row.push({ v: text, t: cellType(cell.t) });
      }
      rows.push(row);
    }

    // Trim fully-empty trailing rows and right-hand columns — sheets
    // routinely carry a used-range far past their real content.
    let lastRow = rows.length - 1;
    while (lastRow >= 0 && rows[lastRow]!.every((c) => c.v === "")) lastRow -= 1;
    rows.length = lastRow + 1;
    let lastCol = -1;
    for (const row of rows) {
      for (let c = row.length - 1; c > lastCol; c -= 1) {
        if (row[c]!.v !== "") {
          lastCol = c;
          break;
        }
      }
    }
    for (const row of rows) row.length = Math.min(row.length, lastCol + 1);

    sheets.push({ name, rows });
  }
  return { sheets, truncated };
}
